/**
 * One side of a knockout box: a crest (kit / flag / fallback shield) above a short team code, with
 * the score (and any penalty score) when the match has been played. The losing side is dimmed.
 * Split out of KnockoutMatchBox so each box renders two of these for home and away.
 */

import React, { useMemo } from 'react'
import { View, Text } from 'react-native'
import { Shield } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag'
import { SoccerKit } from '../../ui/SoccerKit'
import type { BracketTeamSlot } from '../../../src/knockout/types/knockoutBracket.types'
import { compactPlaceholder, toTeamAbbrev } from '../../../src/knockout/helpers/teamAbbrev.helper'
import { BRACKET_CREST_SIZE } from './knockoutBracket.constants'
import { getBracketTeamCellStyles } from './BracketTeamCell.styles'

interface BracketTeamCellProps {
  slot: BracketTeamSlot
  /** Whether the parent match has been played (drives score display + loser dimming). */
  played: boolean
}

export const BracketTeamCell: React.FC<BracketTeamCellProps> = ({ slot, played }) => {
  const theme = useTheme()
  const styles = useMemo(() => getBracketTeamCellStyles(theme), [theme])

  const resolved = slot.name != null && slot.name !== ''
  const loser = played && slot.isWinner === false
  const label = resolved ? toTeamAbbrev(slot) : compactPlaceholder(slot.placeholderLabel)

  const crest = resolved && slot.kit != null ? (
    <SoccerKit primary={slot.kit.primary} secondary={slot.kit.secondary} type={slot.kit.type} size={BRACKET_CREST_SIZE} />
  ) : resolved && slot.countryCode != null && slot.countryCode !== '' ? (
    <CountryFlag countryCode={slot.countryCode} size="mini" accessibilityLabel={slot.name ?? ''} />
  ) : (
    <Shield size={BRACKET_CREST_SIZE} color={theme.gray[300]} fill={theme.gray[200]} strokeWidth={1.5} />
  )

  return (
    <View style={styles.teamCell}>
      <View style={styles.crest}>{crest}</View>
      <Text style={[styles.code, loser && styles.dim]} numberOfLines={1}>
        {label}
      </Text>
      {played && slot.score != null ? (
        <Text style={[styles.score, loser && styles.dim]}>
          {slot.score}
          {slot.penaltyScore != null ? <Text style={styles.penalty}> ({slot.penaltyScore})</Text> : null}
        </Text>
      ) : null}
    </View>
  )
}
